/**
 * AdditionalDocuments.jsx — KaiLux Phase D1: Source Document Enrichment
 *
 * Optional step inserted between TrialBalance/BankStatement and Notes.
 * Lets firms upload source documents that upgrade specific notes from
 * "TB-derived" to "source-verified" — unlocking audit-grade accuracy.
 *
 * The TB-only fast path is preserved: "Skip & Continue" is always visible.
 *
 * Route: /additional-documents
 * Expects: afsJobId in location.state (passed from TrialBalance/BankStatement pages)
 */

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Paperclip } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  uploadSourceDocument,
  getSourceDocuments,
  getOrCreateDraftJob,
  deleteSourceDocument,
  saveManualDocument,
  saveJobProgress,
} from "../api/kailux";
import Icon from '../components/Icon'

import { Dot } from '../components/Icon'
import { T, alpha } from '../theme/theme'
// ---------------------------------------------------------------------------
// Theme
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// Document type definitions
// ---------------------------------------------------------------------------

const HIGH_IMPACT = [
  {
    type: "fixed_asset_register",
    title: "Fixed Asset Register",
    unlocks: "Note 2 — Real PPE roll-forward per asset",
    description:
      "Replaces estimated PPE values with actual per-asset cost, accumulated depreciation, and net book value.",
    accepts: ".xlsx,.xls,.csv,.pdf",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} style={{width:20,height:20}}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
      </svg>
    ),
  },
  {
    type: "prior_year_afs",
    title: "Prior Year Signed AFS",
    unlocks: "Opening balance cross-check across all notes",
    description:
      "Compares prior year closing balances to this year's opening figures and flags any discrepancies.",
    accepts: ".pdf",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} style={{width:20,height:20}}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
      </svg>
    ),
  },
  {
    type: "debtors_age_analysis",
    title: "Debtors Age Analysis",
    unlocks: "Note 5 — Real receivables ageing breakdown",
    description:
      "Replaces the gross receivables estimate with actual current/30/60/90-day ageing buckets.",
    accepts: ".xlsx,.xls,.csv,.pdf",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} style={{width:20,height:20}}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75" />
      </svg>
    ),
  },
  {
    type: "creditors_age_analysis",
    title: "Creditors Age Analysis",
    unlocks: "Note 11 — Real payables ageing breakdown",
    description:
      "Replaces percentage-split estimates with actual creditor balances and ageing.",
    accepts: ".xlsx,.xls,.csv,.pdf",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} style={{width:20,height:20}}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
      </svg>
    ),
  },
  {
    type: "loan_agreement",
    title: "Loan Agreements / Amortisation Schedules",
    unlocks: "Note 9 — Accurate borrowings with rate, term and security",
    description:
      "Extracts lender, interest rate, repayment schedule, security held, and current/non-current split.",
    accepts: ".pdf",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} style={{width:20,height:20}}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0012 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75z" />
      </svg>
    ),
  },
];

const SUPPORTING = [
  {
    type: "board_minutes",
    title: "Minutes of Meetings",
    unlocks: "Notes 19, 20, 21 — Subsequent events, going concern, dividends",
    description:
      "AI extracts dividends declared, going concern resolutions, and post year-end events.",
    accepts: ".pdf",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} style={{width:20,height:20}}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" />
      </svg>
    ),
  },
  {
    type: "cipc_documents",
    title: "CIPC Documents",
    unlocks: "Notes 1, 18 — Entity details and related party disclosures",
    description:
      "Extracts incorporation date, director details, and shareholder information for automatic note population.",
    accepts: ".pdf",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} style={{width:20,height:20}}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zm1.294 6.336a6.721 6.721 0 01-3.17.789 6.721 6.721 0 01-3.168-.789 3.376 3.376 0 016.338 0z" />
      </svg>
    ),
  },
  {
    type: "vat_return",
    title: "VAT201 Returns",
    unlocks: "Note 12 — VAT control account reasonableness check",
    description:
      "Compares VAT returns to the TB VAT control account and flags material differences.",
    accepts: ".pdf",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} style={{width:20,height:20}}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 14.25l6-6m4.5-3.493V21.75l-3.75-1.5-3.75 1.5-3.75-1.5-3.75 1.5V4.757c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0c1.1.128 1.907 1.077 1.907 2.185zM9.75 9h.008v.008H9.75V9zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm4.125 4.5h.008v.008h-.008V13.5zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
      </svg>
    ),
  },
  {
    type: "payroll_return",
    title: "EMP201 / EMP501 Returns",
    unlocks: "Note 16 — Employee costs reasonableness check",
    description:
      "Verifies PAYE, UIF, and SDL figures against the TB employee cost accounts.",
    accepts: ".pdf",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} style={{width:20,height:20}}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
      </svg>
    ),
  },
];

const ALL_DOC_TYPES = [...HIGH_IMPACT, ...SUPPORTING];

// Document types that support manual entry (matches backend MANUAL_ENTRY_SUPPORTED)
const MANUAL_ENTRY_SUPPORTED = new Set([
  "fixed_asset_register",
  "loan_agreement",
  "lease_agreement",
  "board_minutes",
]);

// ---------------------------------------------------------------------------
// Shared manual form primitives — dark themed
// ---------------------------------------------------------------------------

const inputStyle = {
  width: '100%', padding: '7px 10px', background: T.bg, border: `1px solid ${T.bd}`,
  borderRadius:'6px', color: T.tx, fontSize:'13px', outline: 'none',
  fontFamily: "'Outfit', sans-serif", boxSizing: 'border-box',
}

function MField({ label, children, hint }) {
  return (
    <div>
      <label style={{ display:'block', fontSize:'12px', fontWeight:600,
        color: T.lt, marginBottom:'4px' }}>{label}</label>
      {children}
      {hint && <p style={{ fontSize:'10px', color: T.mt, marginTop:'3px' }}>{hint}</p>}
    </div>
  );
}

function MInput({ value, onChange, type = "text", placeholder = "", min, step }) {
  return (
    <input type={type} value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} min={min} step={step} style={inputStyle} />
  );
}

function MSelect({ value, onChange, options }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      style={{ ...inputStyle, cursor: 'pointer' }}>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function MTextarea({ value, onChange, placeholder, rows = 3 }) {
  return (
    <textarea value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} rows={rows}
      style={{ ...inputStyle, resize: 'none', lineHeight: '1.5' }} />
  );
}

function AddRowButton({ onClick, label = "Add row" }) {
  return (
    <button onClick={onClick}
      style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'12px',
        color: T.lt, background: 'none', border: 'none', cursor:'pointer',
        fontFamily:"'Outfit',sans-serif", marginTop:'8px', padding:'4px 0' }}>
      <svg style={{width:12,height:12}} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
      {label}
    </button>
  );
}

function RemoveRowButton({ onClick }) {
  return (
    <button onClick={onClick} title="Remove"
      style={{ background:'none', border:'none', cursor:'pointer',
        color: T.mt, padding:'4px', marginTop:'20px', flexShrink:0 }}>
      <svg style={{width:13,height:13}} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  );
}

const rowCard = {
  background: T.s3, border: `1px solid ${T.bd}`, borderRadius:'8px',
  padding:'12px', marginBottom:'8px', position:'relative',
}

const sectionLabel = {
  fontSize:'10px', fontWeight:700, color: T.lt,
  textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'8px', display:'block',
}

// ---------------------------------------------------------------------------
// Manual form: Fixed Asset Register
// ---------------------------------------------------------------------------

const BLANK_ASSET = {
  name: "", category: "Other", cost: "", accumulated_depreciation: "",
  net_book_value: "", depreciation_charge_current_year: "",
  date_acquired: "", useful_life_years: "", depreciation_rate_percent: "",
  depreciation_method: "Straight-line",
};

const ASSET_CATEGORIES = [
  "Motor Vehicles","Plant & Machinery","Furniture & Fittings",
  "Computer Equipment","Buildings","Land","Other",
];

function ManualFormFixedAsset({ value, onChange }) {
  const rows = value.length ? value : [{ ...BLANK_ASSET }];

  const update = (i, field, val) => {
    const next = rows.map((r, idx) => idx === i ? { ...r, [field]: val } : r);
    onChange(next);
  };
  const addRow    = () => onChange([...rows, { ...BLANK_ASSET }]);
  const removeRow = (i) => onChange(rows.filter((_, idx) => idx !== i));

  return (
    <div>
      {rows.map((asset, i) => (
        <div key={i} style={rowCard}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'8px' }}>
            <span style={sectionLabel}>Asset {i + 1}</span>
            {rows.length > 1 && <RemoveRowButton onClick={() => removeRow(i)} />}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
            <div style={{ gridColumn:'span 2' }}>
              <MField label="Asset description">
                <MInput value={asset.name} onChange={v => update(i, "name", v)} placeholder="e.g. Toyota Hilux" />
              </MField>
            </div>
            <MField label="Category">
              <MSelect value={asset.category} onChange={v => update(i, "category", v)} options={ASSET_CATEGORIES} />
            </MField>
            <MField label="Depreciation method">
              <MSelect value={asset.depreciation_method} onChange={v => update(i, "depreciation_method", v)}
                options={["Straight-line","Diminishing balance","Unknown"]} />
            </MField>
            <MField label="Cost (R)">
              <MInput type="number" value={asset.cost} onChange={v => update(i, "cost", v)} placeholder="0.00" step="0.01" />
            </MField>
            <MField label="Accum. depreciation (R)">
              <MInput type="number" value={asset.accumulated_depreciation} onChange={v => update(i, "accumulated_depreciation", v)} placeholder="0.00" step="0.01" />
            </MField>
            <MField label="Net book value (R)">
              <MInput type="number" value={asset.net_book_value} onChange={v => update(i, "net_book_value", v)} placeholder="0.00" step="0.01" />
            </MField>
            <MField label="Dep. charge current year (R)">
              <MInput type="number" value={asset.depreciation_charge_current_year} onChange={v => update(i, "depreciation_charge_current_year", v)} placeholder="0.00" step="0.01" />
            </MField>
            <MField label="Date acquired">
              <MInput type="date" value={asset.date_acquired} onChange={v => update(i, "date_acquired", v)} />
            </MField>
            <MField label="Useful life (years)">
              <MInput type="number" value={asset.useful_life_years} onChange={v => update(i, "useful_life_years", v)} min="0" placeholder="5" />
            </MField>
          </div>
        </div>
      ))}
      <AddRowButton onClick={addRow} label="Add asset" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Manual form: Loan Agreements
// ---------------------------------------------------------------------------

const BLANK_LOAN = {
  lender: "", facility_type: "Term loan", principal_amount: "",
  outstanding_balance: "", interest_rate_description: "",
  interest_rate_type: "Variable", term_months: "", monthly_installment: "",
  security_description: "", current_portion: "", non_current_portion: "",
  repayment_date: "", covenant_summary: "",
};

function ManualFormLoan({ value, onChange }) {
  const rows = value.length ? value : [{ ...BLANK_LOAN }];

  const update = (i, field, val) => {
    onChange(rows.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
  };
  const addRow    = () => onChange([...rows, { ...BLANK_LOAN }]);
  const removeRow = (i) => onChange(rows.filter((_, idx) => idx !== i));

  return (
    <div>
      {rows.map((loan, i) => (
        <div key={i} style={rowCard}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'8px' }}>
            <span style={sectionLabel}>Loan {i + 1}</span>
            {rows.length > 1 && <RemoveRowButton onClick={() => removeRow(i)} />}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
            <MField label="Lender">
              <MInput value={loan.lender} onChange={v => update(i, "lender", v)} placeholder="e.g. FNB Business Bank" />
            </MField>
            <MField label="Facility type">
              <MSelect value={loan.facility_type} onChange={v => update(i, "facility_type", v)}
                options={["Term loan","Overdraft","Revolving credit","Asset finance","Mortgage bond"]} />
            </MField>
            <MField label="Principal amount (R)">
              <MInput type="number" value={loan.principal_amount} onChange={v => update(i, "principal_amount", v)} placeholder="0.00" step="0.01" />
            </MField>
            <MField label="Outstanding balance (R)">
              <MInput type="number" value={loan.outstanding_balance} onChange={v => update(i, "outstanding_balance", v)} placeholder="0.00" step="0.01" />
            </MField>
            <MField label="Interest rate">
              <MInput value={loan.interest_rate_description} onChange={v => update(i, "interest_rate_description", v)} placeholder="e.g. Prime + 2% p.a." />
            </MField>
            <MField label="Rate type">
              <MSelect value={loan.interest_rate_type} onChange={v => update(i, "interest_rate_type", v)}
                options={["Fixed","Variable","Prime-linked"]} />
            </MField>
            <MField label="Term (months)">
              <MInput type="number" value={loan.term_months} onChange={v => update(i, "term_months", v)} min="0" placeholder="60" />
            </MField>
            <MField label="Monthly instalment (R)">
              <MInput type="number" value={loan.monthly_installment} onChange={v => update(i, "monthly_installment", v)} placeholder="0.00" step="0.01" />
            </MField>
            <MField label="Current portion (R)" hint="Due within 12 months of year-end">
              <MInput type="number" value={loan.current_portion} onChange={v => update(i, "current_portion", v)} placeholder="0.00" step="0.01" />
            </MField>
            <MField label="Non-current portion (R)">
              <MInput type="number" value={loan.non_current_portion} onChange={v => update(i, "non_current_portion", v)} placeholder="0.00" step="0.01" />
            </MField>
            <MField label="Repayment date">
              <MInput type="date" value={loan.repayment_date} onChange={v => update(i, "repayment_date", v)} />
            </MField>
            <MField label="Security held">
              <MInput value={loan.security_description} onChange={v => update(i, "security_description", v)} placeholder="e.g. Registered mortgage bond" />
            </MField>
            <div style={{ gridColumn:'span 2' }}>
              <MField label="Covenant summary (optional)">
                <MInput value={loan.covenant_summary} onChange={v => update(i, "covenant_summary", v)} placeholder="e.g. Maintain debt service cover ratio of 1.5x" />
              </MField>
            </div>
          </div>
        </div>
      ))}
      <AddRowButton onClick={addRow} label="Add loan" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Manual form: Lease Agreements
// ---------------------------------------------------------------------------

const BLANK_LEASE = {
  description: "", lessor: "", lease_type: "Operating lease",
  commencement_date: "", expiry_date: "", term_months: "",
  monthly_payment: "", annual_payment: "", escalation_rate_percent: "",
  purchase_option: false, purchase_option_price: "",
  future_minimum_payments: { within_1_year: "", between_1_and_5_years: "", after_5_years: "" },
};

function ManualFormLease({ value, onChange }) {
  const rows = value.length ? value : [{ ...BLANK_LEASE, future_minimum_payments: { within_1_year: "", between_1_and_5_years: "", after_5_years: "" } }];

  const update = (i, field, val) => {
    onChange(rows.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
  };
  const updateFmp = (i, field, val) => {
    onChange(rows.map((r, idx) => idx === i
      ? { ...r, future_minimum_payments: { ...r.future_minimum_payments, [field]: val } }
      : r));
  };
  const addRow    = () => onChange([...rows, { ...BLANK_LEASE, future_minimum_payments: { within_1_year: "", between_1_and_5_years: "", after_5_years: "" } }]);
  const removeRow = (i) => onChange(rows.filter((_, idx) => idx !== i));

  return (
    <div>
      {rows.map((lease, i) => (
        <div key={i} style={rowCard}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'8px' }}>
            <span style={sectionLabel}>Lease {i + 1}</span>
            {rows.length > 1 && <RemoveRowButton onClick={() => removeRow(i)} />}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
            <MField label="What is being leased">
              <MInput value={lease.description} onChange={v => update(i, "description", v)} placeholder="e.g. Office premises" />
            </MField>
            <MField label="Lessor">
              <MInput value={lease.lessor} onChange={v => update(i, "lessor", v)} placeholder="e.g. Growthpoint Properties" />
            </MField>
            <MField label="Lease type">
              <MSelect value={lease.lease_type} onChange={v => update(i, "lease_type", v)}
                options={["Operating lease","Finance lease"]} />
            </MField>
            <MField label="Escalation rate (% p.a.)">
              <MInput type="number" value={lease.escalation_rate_percent} onChange={v => update(i, "escalation_rate_percent", v)} placeholder="8" step="0.1" />
            </MField>
            <MField label="Commencement date">
              <MInput type="date" value={lease.commencement_date} onChange={v => update(i, "commencement_date", v)} />
            </MField>
            <MField label="Expiry date">
              <MInput type="date" value={lease.expiry_date} onChange={v => update(i, "expiry_date", v)} />
            </MField>
            <MField label="Monthly payment (R)">
              <MInput type="number" value={lease.monthly_payment} onChange={v => update(i, "monthly_payment", v)} placeholder="0.00" step="0.01" />
            </MField>
            <MField label="Annual payment (R)">
              <MInput type="number" value={lease.annual_payment} onChange={v => update(i, "annual_payment", v)} placeholder="0.00" step="0.01" />
            </MField>
            <div style={{ gridColumn:'span 2' }}>
              <p style={sectionLabel}>Future minimum payments (IFRS for SMEs S20)</p>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'8px' }}>
                <MField label="Within 1 year (R)">
                  <MInput type="number" value={lease.future_minimum_payments.within_1_year}
                    onChange={v => updateFmp(i, "within_1_year", v)} placeholder="0.00" step="0.01" />
                </MField>
                <MField label="1–5 years (R)">
                  <MInput type="number" value={lease.future_minimum_payments.between_1_and_5_years}
                    onChange={v => updateFmp(i, "between_1_and_5_years", v)} placeholder="0.00" step="0.01" />
                </MField>
                <MField label="After 5 years (R)">
                  <MInput type="number" value={lease.future_minimum_payments.after_5_years}
                    onChange={v => updateFmp(i, "after_5_years", v)} placeholder="0.00" step="0.01" />
                </MField>
              </div>
            </div>
          </div>
        </div>
      ))}
      <AddRowButton onClick={addRow} label="Add lease" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Manual form: Board Minutes
// ---------------------------------------------------------------------------

const BLANK_MINUTES = {
  meeting_type: "Board meeting", meeting_date: "",
  dividends_declared: false, dividends_amount_total: "", dividends_amount_per_share: "",
  dividends_declaration_date: "", dividends_payment_date: "",
  going_concern_conclusion: "Positive — entity will continue as going concern",
  going_concern_key_statement: "", going_concern_specific_concerns: "",
  subsequent_events: [], other_material_resolutions: [],
};

const BLANK_EVENT = { event_type: "Non-adjusting", description: "", financial_impact_rands: "" };

function ManualFormMinutes({ value, onChange }) {
  const m = Object.keys(value).length ? value : { ...BLANK_MINUTES };

  const set = (field, val) => onChange({ ...m, [field]: val });

  const addEvent    = () => set("subsequent_events", [...(m.subsequent_events || []), { ...BLANK_EVENT }]);
  const updateEvent = (i, field, val) => set("subsequent_events",
    (m.subsequent_events || []).map((e, idx) => idx === i ? { ...e, [field]: val } : e));
  const removeEvent = (i) => set("subsequent_events",
    (m.subsequent_events || []).filter((_, idx) => idx !== i));

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
      {/* Meeting info */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
        <MField label="Meeting type">
          <MSelect value={m.meeting_type} onChange={v => set("meeting_type", v)}
            options={["Board meeting","AGM","EGM","Special board meeting"]} />
        </MField>
        <MField label="Meeting date">
          <MInput type="date" value={m.meeting_date} onChange={v => set("meeting_date", v)} />
        </MField>
      </div>

      {/* Dividends */}
      <div style={rowCard}>
        <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'8px' }}>
          <input type="checkbox" checked={!!m.dividends_declared}
            onChange={e => set("dividends_declared", e.target.checked)}
            style={{ width:'14px', height:'14px', accentColor: T.gn, cursor:'pointer' }} />
          <span style={{ fontSize:'13px', fontWeight:600, color: T.tx }}>Dividends declared</span>
        </div>
        {m.dividends_declared && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginTop:'8px' }}>
            <MField label="Total amount (R)">
              <MInput type="number" value={m.dividends_amount_total} onChange={v => set("dividends_amount_total", v)} placeholder="0.00" step="0.01" />
            </MField>
            <MField label="Amount per share (R)">
              <MInput type="number" value={m.dividends_amount_per_share} onChange={v => set("dividends_amount_per_share", v)} placeholder="0.00" step="0.01" />
            </MField>
            <MField label="Declaration date">
              <MInput type="date" value={m.dividends_declaration_date} onChange={v => set("dividends_declaration_date", v)} />
            </MField>
            <MField label="Payment date">
              <MInput type="date" value={m.dividends_payment_date} onChange={v => set("dividends_payment_date", v)} />
            </MField>
          </div>
        )}
      </div>

      {/* Going concern */}
      <div style={rowCard}>
        <span style={sectionLabel}>Going Concern Assessment</span>
        <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
          <MField label="Conclusion">
            <MSelect value={m.going_concern_conclusion} onChange={v => set("going_concern_conclusion", v)}
              options={[
                "Positive — entity will continue as going concern",
                "Concern raised",
                "Not explicitly discussed",
              ]} />
          </MField>
          <MField label="Key resolution / statement (optional)">
            <MTextarea value={m.going_concern_key_statement} onChange={v => set("going_concern_key_statement", v)}
              placeholder="e.g. Directors confirm the entity has adequate resources to continue operating for the foreseeable future." rows={2} />
          </MField>
          <MField label="Specific concerns noted (optional)">
            <MInput value={m.going_concern_specific_concerns} onChange={v => set("going_concern_specific_concerns", v)}
              placeholder="e.g. Negative working capital, covenant breach" />
          </MField>
        </div>
      </div>

      {/* Subsequent events */}
      <div>
        <span style={sectionLabel}>Events After Reporting Date (optional)</span>
        {(m.subsequent_events || []).map((ev, i) => (
          <div key={i} style={{ ...rowCard, marginBottom:'8px' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'8px' }}>
              <span style={{ fontSize:'12px', color: T.mt }}>Event {i + 1}</span>
              <RemoveRowButton onClick={() => removeEvent(i)} />
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
              <MField label="Type">
                <MSelect value={ev.event_type} onChange={v => updateEvent(i, "event_type", v)}
                  options={["Adjusting","Non-adjusting"]} />
              </MField>
              <MField label="Financial impact (R, optional)">
                <MInput type="number" value={ev.financial_impact_rands} onChange={v => updateEvent(i, "financial_impact_rands", v)} placeholder="0.00" step="0.01" />
              </MField>
              <div style={{ gridColumn:'span 2' }}>
                <MField label="Description">
                  <MTextarea value={ev.description} onChange={v => updateEvent(i, "description", v)}
                    placeholder="Describe the event that occurred after year-end." rows={2} />
                </MField>
              </div>
            </div>
          </div>
        ))}
        <AddRowButton onClick={addEvent} label="Add subsequent event" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Manual form dispatcher
// ---------------------------------------------------------------------------

function ManualForm({ docType, value, onChange }) {
  switch (docType) {
    case "fixed_asset_register":
      return <ManualFormFixedAsset value={value || []} onChange={onChange} />;
    case "loan_agreement":
      return <ManualFormLoan value={value || []} onChange={onChange} />;
    case "lease_agreement":
      return <ManualFormLease value={value || []} onChange={onChange} />;
    case "board_minutes":
      return <ManualFormMinutes value={value || {}} onChange={onChange} />;
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Status Pill
// ---------------------------------------------------------------------------

function StatusPill({ status, entryMethod }) {
  const config = {
    idle:       { label: "Not uploaded",  bg: T.s3,    color: T.mt },
    uploading:  { label: "Uploading…",    bg: T.bld,   color: T.bl },
    processing: { label: "Extracting…",   bg: T.amd,   color: T.am },
    verified:   { label: entryMethod === "manual" ? "Verified (manual)" : "Verified",
                  bg: T.gnd, color: T.gn },
    failed:     { label: "Failed",        bg: T.rdd,   color: T.rd },
  };
  const c = config[status] || config.idle;
  return (
    <span title={status === "verified"
      ? (entryMethod === "manual" ? "Source-verified — manually entered" : "Source-verified — document uploaded")
      : undefined}
      style={{ display:'inline-flex', alignItems:'center', gap:'4px',
        padding:'3px 8px', borderRadius:'999px', fontSize:'12px', fontWeight:600,
        background: c.bg, color: c.color, whiteSpace:'nowrap' }}>
      {status === "verified" && (
        <svg viewBox="0 0 20 20" fill="currentColor" style={{width:11,height:11}}>
          <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
        </svg>
      )}
      {status === "processing" && (
        <svg fill="none" viewBox="0 0 24 24" style={{width:11,height:11,animation:'spin 1s linear infinite'}}>
          <circle style={{opacity:0.25}} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path style={{opacity:0.75}} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      )}
      {c.label}
    </span>
  );
}

function CheckIcon({ style: s }) {
  return (
    <svg style={s} viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
    </svg>
  );
}

function SpinnerIcon({ style: s }) {
  return (
    <svg fill="none" viewBox="0 0 24 24" style={s}>
      <circle style={{opacity:0.25}} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path style={{opacity:0.75}} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Extracted data preview
// ---------------------------------------------------------------------------

function ExtractedDataPreview({ docType, data }) {
  const [open, setOpen] = useState(false);

  const summary = useCallback(() => {
    if (!data) return null;
    switch (docType) {
      case "fixed_asset_register": {
        const count = data.assets?.length ?? 0;
        const total = data.totals?.net_book_value ?? 0;
        return `${count} asset${count !== 1 ? "s" : ""} — net book value R${total.toLocaleString("en-ZA")}`;
      }
      case "prior_year_afs": {
        const entity = data.entity_name ?? "—";
        const ye = data.year_end ?? "—";
        return `${entity} · Year end ${ye}`;
      }
      case "debtors_age_analysis":
      case "creditors_age_analysis": {
        const total = data.total_gross ?? data.total ?? 0;
        const over90 = data.buckets?.over_90_days ?? 0;
        return `Total R${total.toLocaleString("en-ZA")} · Over 90 days R${over90.toLocaleString("en-ZA")}`;
      }
      case "loan_agreement": {
        const count = data.loans?.length ?? 0;
        const total = data.totals?.total_outstanding ?? 0;
        return `${count} loan${count !== 1 ? "s" : ""} · Outstanding R${total.toLocaleString("en-ZA")}`;
      }
      case "board_minutes": {
        const div = data.dividends?.declared ? `Dividends R${(data.dividends.amount_total ?? 0).toLocaleString("en-ZA")}` : "No dividends";
        const gc = data.going_concern?.conclusion ?? "—";
        return `${div} · Going concern: ${gc}`;
      }
      case "cipc_documents": {
        const name = data.company?.registered_name ?? "—";
        const dirs = data.directors?.filter(d => d.is_current).length ?? 0;
        return `${name} · ${dirs} current director${dirs !== 1 ? "s" : ""}`;
      }
      case "vat_return": {
        const total = data.totals?.total_output_vat ?? 0;
        const periods = data.periods?.length ?? 0;
        return `${periods} period${periods !== 1 ? "s" : ""} · Output VAT R${total.toLocaleString("en-ZA")}`;
      }
      case "payroll_return": {
        const paye = data.totals?.total_paye ?? 0;
        const emp = data.totals?.average_employees ?? 0;
        return `PAYE R${paye.toLocaleString("en-ZA")} · Avg ${emp} employee${emp !== 1 ? "s" : ""}`;
      }
      case "lease_agreement": {
        const count = data.leases?.length ?? 0;
        return `${count} lease arrangement${count !== 1 ? "s" : ""}`;
      }
      default:
        return "Extraction complete";
    }
  }, [docType, data]);

  return (
    <div style={{ marginTop:'10px', border:`1px solid ${alpha(T.gn, 0.19)}`, borderRadius:'8px', overflow:'hidden' }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'7px 10px', background: T.gnd, border:'none', cursor:'pointer', textAlign:'left' }}>
        <span style={{ fontSize:'12px', color: T.gn, fontWeight:600 }}>{summary()}</span>
        <svg style={{ width:14, height:14, color: T.gn,
          transform: open ? 'rotate(180deg)' : 'none', transition:'transform 0.2s' }}
          fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div style={{ padding:'8px 10px', background: T.s3, maxHeight:'160px', overflowY:'auto' }}>
          <pre style={{ fontSize:'10px', color: T.lt, whiteSpace:'pre-wrap',
            fontFamily:"'DM Mono',monospace", lineHeight:'1.5' }}>
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Document Card
// ---------------------------------------------------------------------------

function DocumentCard({ def, state, onUpload, onDelete, onManualSave }) {
  const inputRef = useRef(null);
  const [dragging,      setDragging]      = useState(false);
  const supportsManual = MANUAL_ENTRY_SUPPORTED.has(def.type);
  const [tab,           setTab]           = useState("upload");
  const [manualData,    setManualData]    = useState(null);
  const [manualSaving,  setManualSaving]  = useState(false);
  const [manualError,   setManualError]   = useState(null);

  const { status, fileName, extractedData, error, uploadProgress, entryMethod, uploadedByEmail } = state;

  const handleFile = (file) => { if (!file) return; onUpload(def.type, file); };
  const handleDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };
  const handleManualSave = async () => {
    setManualSaving(true); setManualError(null);
    try { await onManualSave(def.type, manualData); }
    catch (e) { setManualError(e.response?.data?.detail || e.message || "Save failed — please try again."); }
    finally { setManualSaving(false); }
  };

  const isActive = status === "verified";
  const isBusy   = status === "uploading" || status === "processing";

  return (
    <div style={{
      display:'flex', flexDirection:'column', borderRadius:'12px', overflow:'hidden',
      border: `1px solid ${isActive ? alpha(T.gn, 0.31) : T.bd}`,
      background: isActive ? T.gnd : T.s2,
      transition:'border-color 0.2s',
    }}>
      {/* Card header */}
      <div style={{ padding:'14px 14px 10px', display:'flex', alignItems:'flex-start', gap:'8px' }}>
        <div style={{ padding:'8px', borderRadius:'8px', flexShrink:0,
          background: isActive ? T.gnd : T.s3, color: isActive ? T.gn : T.lt }}>
          {def.icon}
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'8px' }}>
            <h3 style={{ fontSize:'14px', fontWeight:700, color: T.tx, lineHeight:'1.3', margin:0 }}>{def.title}</h3>
            <StatusPill status={status} entryMethod={entryMethod} />
          </div>
          <p style={{ fontSize:'12px', color: T.gn, fontWeight:600, marginTop:'3px' }}>{def.unlocks}</p>
          <p style={{ fontSize:'12px', color: T.mt, marginTop:'4px', lineHeight:'1.5' }}>{def.description}</p>
        </div>
      </div>

      {/* Tab toggle */}
      {supportsManual && status !== "verified" && (
        <div style={{ padding:'0 14px 8px' }}>
          <div style={{ display:'inline-flex', border:`1px solid ${T.bd}`, borderRadius:'8px', overflow:'hidden' }}>
            {[["upload","Upload file"],["manual","Enter manually"]].map(([key, label]) => (
              <button key={key} onClick={() => setTab(key)}
                style={{ padding:'6px 12px', fontSize:'12px', fontWeight:600, cursor:'pointer',
                  border:'none', fontFamily:"'Outfit',sans-serif", transition:'all 0.15s',
                  background: tab === key ? T.prg : 'transparent',
                  color: tab === key ? T.onpri : T.mt,
                }}>
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Content area */}
      <div style={{ padding:'0 14px 14px' }}>
        {status === "verified" ? (
          <div>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                <div style={{ width:'18px', height:'18px', borderRadius:'50%', background: T.gnd,
                  display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <CheckIcon style={{ width:10, height:10, color: T.gn }} />
                </div>
                <span style={{ fontSize:'12px', color: T.tx, fontWeight:600,
                  overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'180px' }}>
                  {entryMethod === "manual" ? "Manually entered" : (fileName || "Document uploaded")}
                </span>
              </div>
              <button onClick={() => onDelete(def.type)} title="Remove"
                style={{ background:'none', border:'none', cursor:'pointer', color: T.mt,
                  padding:'4px', borderRadius:'6px' }}>
                <svg style={{width:14,height:14}} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {uploadedByEmail && (
              <div style={{ fontSize:'11px', color: T.mt, marginTop:'4px', marginLeft:'26px' }}>
                {entryMethod === "manual" ? "Entered" : "Uploaded"} by {uploadedByEmail}
              </div>
            )}
            {extractedData && <ExtractedDataPreview docType={def.type} data={extractedData} />}
          </div>
        ) : isBusy ? (
          <div style={{ display:'flex', alignItems:'center', gap:'8px', padding:'10px 0' }}>
            <SpinnerIcon style={{ width:14, height:14, color: T.bl, animation:'spin 1s linear infinite', flexShrink:0 }} />
            <div style={{ flex:1 }}>
              <p style={{ fontSize:'12px', color: T.tx, fontWeight:600 }}>
                {status === "uploading" ? `Uploading ${fileName}…` : "Extracting data with AI…"}
              </p>
              {status === "uploading" && uploadProgress != null && (
                <div style={{ marginTop:'6px', height:'3px', background: T.bd, borderRadius:'999px', overflow:'hidden' }}>
                  <div style={{ width:`${uploadProgress}%`, height:'100%', background: T.bl,
                    borderRadius:'999px', transition:'width 0.3s' }} />
                </div>
              )}
            </div>
          </div>
        ) : tab === "upload" || !supportsManual ? (
          <>
            <div
              onClick={() => !isBusy && inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
                gap:'4px', padding:'16px', borderRadius:'8px', cursor:'pointer', userSelect:'none',
                border:`2px dashed ${dragging ? T.or : T.bd}`,
                background: dragging ? T.orb : T.s3,
                transition:'all 0.15s' }}
            >
              <svg style={{width:18,height:18,color:T.lt}} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
              </svg>
              <span style={{ fontSize:'12px', color: T.mt }}>
                Drop file or <span style={{ color: T.tx, fontWeight:600 }}>browse</span>
              </span>
              <span style={{ fontSize:'10px', color: T.mt }}>
                {def.accepts.replace(/\./g, "").replace(/,/g, ", ").toUpperCase()}
              </span>
            </div>
            {status === "failed" && error && (
              <p style={{ marginTop:'7px', fontSize:'12px', color: T.rd }}>{error}</p>
            )}
            <input ref={inputRef} type="file" accept={def.accepts} style={{ display:'none' }}
              onChange={(e) => handleFile(e.target.files?.[0])} />
          </>
        ) : (
          <div style={{ marginTop:'4px' }}>
            <ManualForm docType={def.type} value={manualData} onChange={setManualData} />
            {manualError && (
              <p style={{ marginTop:'7px', fontSize:'12px', color: T.rd }}>{manualError}</p>
            )}
            <button onClick={handleManualSave} disabled={manualSaving}
              style={{ marginTop:'10px', width:'100%', padding:'8px',
                background: manualSaving ? T.mt : T.prg, color:T.onpri,
                border:'none', borderRadius:'8px', fontSize:'13px', fontWeight:700,
                cursor: manualSaving ? 'not-allowed' : 'pointer',
                fontFamily:"'Outfit',sans-serif", display:'flex', alignItems:'center',
                justifyContent:'center', gap:'4px', opacity: manualSaving ? 0.7 : 1 }}>
              {manualSaving ? (
                <><SpinnerIcon style={{width:12,height:12,color:T.onpri,animation:'spin 1s linear infinite'}} /> Saving…</>
              ) : "Save & verify →"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Default doc state
// ---------------------------------------------------------------------------

const DEFAULT_STATE = {
  status: "idle",
  fileName: null,
  extractedData: null,
  error: null,
  id: null,
  uploadProgress: null,
  entryMethod: null,
  uploadedByEmail: "",
  // FEATURE (Sep 2026): balance-sheet figures derivable from this specific
  // document (see backend document_to_afs_figures()) -- {} for document
  // types that are disclosure-only. Populated from the upload/manual-save
  // response, or re-derived server-side on load for documents from an
  // earlier session (see get_source_documents()).
  figuresPartial: {},
  figuresNotes: [],
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function AdditionalDocuments() {
  const navigate = useNavigate();
  const location = useLocation();
  const { firm }  = useAuth();

  const {
    afsJobId: stateAfsJobId,
    clientId: stateClientId,
    clientName,
    client,
    mappings,
    accounts,
    figures,
    prior_figures,
    first_year,
    prior_not_available,
    financial_year,
    year_end: stateYearEnd,
    period_start: statePeriodStart,
    // FIX (kailux-fixes-log.md — "Back to Mapping loses all trial-balance
    // progress"): TrialBalance.jsx's own resume-hydration effect expects
    // ISO-format period_start/period_end, but year_end/period_start above
    // are display-formatted (e.g. "28 Feb 2026") — passing those back would
    // silently corrupt the date picker. period_start_iso/period_end_iso
    // carry the real ISO values through so a later "Back to Mapping" click
    // (added downstream in Notes.jsx) can restore the actually-selected
    // period instead of falling back to today's default.
    period_start_iso,
    period_end_iso,
    returnState,
    // FIX (Sep 2026 — bank-statement flow now routes through here too, see
    // BankStatementBalanceSheet.jsx): carried through so proceed() below
    // can forward it to Notes exactly as the trial-balance flow already
    // forwards its own (absent) source flag, and so Notes.jsx's Back
    // button knows to return to the balance-sheet screen instead of
    // /trial-balance.
    source,
    bank_statement_source = false,
    // Bug fix (Sep 2026) — TrialBalance.jsx's step-boundary autosave can
    // fail (network hiccup, or the job couldn't be created/found) without
    // blocking the preparer from continuing on here — that's intentional.
    // But it used to fail completely silently. Now it's flagged through so
    // we can at least warn: if they leave before finishing, there's no
    // saved checkpoint to resume the trial balance categorization from.
    autosave_failed: autosaveFailed,
  } = location.state || {}

  const clientId    = stateClientId || client?.id
  const clientLabel = clientName    || client?.name || ''
  const financialYear = financial_year || String(new Date().getFullYear())
  // Phase 1 (Jul 2026): explicit period from the free date-range picker,
  // forwarded the same way afsJobId/financial_year already are (see
  // kailux-fixes-log.md §3.3 — the pattern of falling back to sessionStorage
  // is deliberate, not accidental duplication).
  const yearEnd     = stateYearEnd     || sessionStorage.getItem('kailux:year_end') || ''
  const periodStart = statePeriodStart || sessionStorage.getItem('kailux:period_start_display') || ''

  if (!clientId) {
    return (
      <div style={{ minHeight:'100vh', background: T.bg, display:'flex',
        alignItems:'center', justifyContent:'center', fontFamily:"'Outfit',sans-serif" }}>
        <div style={{ textAlign:'center', padding:'40px', maxWidth:'400px' }}>
          <div style={{ marginBottom:'16px' }}><Paperclip size={40} strokeWidth={1.5} color={T.mt}/></div>
          <div style={{ fontSize:'18px', fontWeight:'700', color: T.tx, marginBottom:'10px' }}>
            No client selected
          </div>
          <div style={{ fontSize:'14px', color: T.mt, lineHeight:'1.7', marginBottom:'24px' }}>
            Start from a client's trial balance upload to reach this step.
          </div>
          <button onClick={() => navigate('/clients')}
            style={{ padding:'11px 28px', borderRadius:'8px', border:'none',
              background:T.prg, color:T.onpri,
              fontFamily:"'Outfit',sans-serif", fontSize:'14px', fontWeight:'700', cursor:'pointer' }}>
            Go to Clients
          </button>
        </div>
      </div>
    )
  }

  const firmId = firm?.id;

  const [docs, setDocs] = useState(
    Object.fromEntries(ALL_DOC_TYPES.map(d => [d.type, { ...DEFAULT_STATE }]))
  );
  const [loading,   setLoading]   = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [resolvedAfsJobId, setResolvedAfsJobId] = useState(stateAfsJobId || null);
  const [jobError, setJobError] = useState(null);
  // Bug fix (Sep 2026) — same re-entrancy risk as TrialBalance.jsx's
  // proceedToDocuments: "Skip & Continue" and "Done — Return to AFS ->"
  // both call proceed() directly with no guard against a double-fire. The
  // actual guard must be a ref, not just this state: two clicks in the
  // same tick (e.g. a real double-click) both run before React re-renders
  // with the updated state, so each would read the same stale `false` from
  // useState's closure. isProceeding (state) is for the button's visual
  // disabled/label only; isProceedingRef is the real, synchronous guard.
  const [isProceeding, setIsProceeding] = useState(false);
  const isProceedingRef = useRef(false);

  useEffect(() => {
    if (stateAfsJobId || !clientId || !firmId) return;
    getOrCreateDraftJob(clientId, firmId, financialYear, periodStart, yearEnd)
      .then(({ afs_job_id }) => setResolvedAfsJobId(afs_job_id))
      .catch(e => setJobError(e.response?.data?.detail || e.message || "Could not start this AFS job."));
  }, [stateAfsJobId, clientId, firmId, financialYear, periodStart, yearEnd]);

  useEffect(() => {
    if (!resolvedAfsJobId) { setLoading(false); return; }
    getSourceDocuments(resolvedAfsJobId)
      .then(({ documents }) => {
        if (!documents?.length) return;
        setDocs(prev => {
          const next = { ...prev };
          documents.forEach(doc => {
            next[doc.document_type] = {
              status: doc.extraction_status === "verified" ? "verified"
                    : doc.extraction_status === "failed"   ? "failed"
                    : "idle",
              fileName: doc.file_name,
              extractedData: doc.extracted_data,
              error: doc.extraction_status === "failed" ? "Extraction failed — try re-uploading." : null,
              id: doc.id,
              uploadProgress: null,
              entryMethod: doc.entry_method || "upload",
              uploadedByEmail: doc.uploaded_by_email || "",
              figuresPartial: doc.figures_partial || {},
              figuresNotes: doc.figures_notes || [],
            };
          });
          return next;
        });
      })
      .catch(e => setLoadError(e.message))
      .finally(() => setLoading(false));
  }, [resolvedAfsJobId]);

  const handleUpload = useCallback(async (docType, file) => {
    if (!resolvedAfsJobId) { alert("Still preparing this AFS job — please wait a moment and try again."); return; }

    setDocs(prev => ({
      ...prev,
      [docType]: { ...DEFAULT_STATE, status: "uploading", fileName: file.name, uploadProgress: 0 },
    }));

    try {
      const result = await uploadSourceDocument(
        resolvedAfsJobId, docType, file,
        (pct) => setDocs(prev => ({
          ...prev,
          [docType]: { ...prev[docType], uploadProgress: pct },
        }))
      );
      setDocs(prev => ({ ...prev, [docType]: { ...prev[docType], status: "processing", uploadProgress: null } }));
      setDocs(prev => ({
        ...prev,
        [docType]: { status:"verified", fileName:file.name, extractedData:result.extracted_data,
          error:null, id:result.id, uploadProgress:null, entryMethod:"upload",
          uploadedByEmail: result.uploaded_by_email || "",
          figuresPartial: result.figures_partial || {}, figuresNotes: result.figures_notes || [] },
      }));
    } catch (e) {
      const message = e.response?.data?.detail || e.message || "Upload failed. Please try again.";
      setDocs(prev => ({
        ...prev,
        [docType]: { ...DEFAULT_STATE, status:"failed", fileName:file.name, error:message },
      }));
    }
  }, [resolvedAfsJobId]);

  const handleManualSave = useCallback(async (docType, formData) => {
    if (!resolvedAfsJobId || !firmId) throw new Error("Still preparing this AFS job — please wait a moment and try again.");

    let payload = {};
    if (docType === "fixed_asset_register") {
      payload = { assets: (formData || []).map(a => ({
        ...a,
        cost: parseFloat(a.cost) || 0,
        accumulated_depreciation: parseFloat(a.accumulated_depreciation) || 0,
        net_book_value: parseFloat(a.net_book_value) || 0,
        depreciation_charge_current_year: parseFloat(a.depreciation_charge_current_year) || 0,
        useful_life_years: parseInt(a.useful_life_years) || 0,
        depreciation_rate_percent: parseFloat(a.depreciation_rate_percent) || 0,
      })) };
    } else if (docType === "loan_agreement") {
      payload = { loans: (formData || []).map(l => ({
        ...l,
        principal_amount: parseFloat(l.principal_amount) || 0,
        outstanding_balance: parseFloat(l.outstanding_balance) || 0,
        term_months: parseInt(l.term_months) || 0,
        monthly_installment: parseFloat(l.monthly_installment) || 0,
        current_portion: parseFloat(l.current_portion) || 0,
        non_current_portion: parseFloat(l.non_current_portion) || 0,
      })) };
    } else if (docType === "lease_agreement") {
      payload = { leases: (formData || []).map(l => ({
        ...l,
        term_months: parseInt(l.term_months) || 0,
        monthly_payment: parseFloat(l.monthly_payment) || 0,
        annual_payment: parseFloat(l.annual_payment) || 0,
        escalation_rate_percent: parseFloat(l.escalation_rate_percent) || 0,
        purchase_option_price: parseFloat(l.purchase_option_price) || 0,
        future_minimum_payments: {
          within_1_year: parseFloat(l.future_minimum_payments?.within_1_year) || 0,
          between_1_and_5_years: parseFloat(l.future_minimum_payments?.between_1_and_5_years) || 0,
          after_5_years: parseFloat(l.future_minimum_payments?.after_5_years) || 0,
        },
      })) };
    } else if (docType === "board_minutes") {
      const m = formData || {};
      payload = { minutes: {
        ...m,
        dividends_amount_total: parseFloat(m.dividends_amount_total) || 0,
        dividends_amount_per_share: parseFloat(m.dividends_amount_per_share) || 0,
        subsequent_events: (m.subsequent_events || []).map(e => ({
          ...e,
          financial_impact_rands: parseFloat(e.financial_impact_rands) || 0,
        })),
      }};
    }

    const result = await saveManualDocument(resolvedAfsJobId, docType, payload);
    setDocs(prev => ({
      ...prev,
      [docType]: { status:"verified", fileName:null, extractedData:result.extracted_data,
        error:null, id:result.id, uploadProgress:null, entryMethod:"manual",
        uploadedByEmail: result.uploaded_by_email || "",
        figuresPartial: result.figures_partial || {}, figuresNotes: result.figures_notes || [] },
    }));
  }, [resolvedAfsJobId]);

  const handleDelete = useCallback(async (docType) => {
    const docId = docs[docType]?.id;
    if (docId) { try { await deleteSourceDocument(docId); } catch { /* non-fatal */ } }
    setDocs(prev => ({ ...prev, [docType]: { ...DEFAULT_STATE } }));
  }, [docs]);

  const verifiedCount = Object.values(docs).filter(d => d.status === "verified").length;

  // FEATURE (Sep 2026): balance-sheet figures pulled from uploaded source
  // documents (see backend document_to_afs_figures()) as pre-fill/cross-
  // check candidates against whatever figures already exist at this point
  // (trial-balance-mapped figures for the TB flow; for the bank-statement
  // flow, this page now runs BEFORE the balance-sheet screen, so `figures`
  // here is typically empty for these specific keys and every document
  // value below is pure pre-fill, not a conflict).
  //
  // Only two document types currently produce SFP figures at all (see
  // document_to_afs_figures()'s own docstring for why lease_agreement and
  // the rest don't), and between them there is no overlapping figure key,
  // so each key can only ever come from one document — conflicts are
  // strictly "document value vs whatever figures already had", never
  // "document A vs document B".
  const documentFigures = useMemo(() => {
    const out = {};
    ALL_DOC_TYPES.forEach(({ type, title }) => {
      const d = docs[type];
      if (d?.status !== "verified" || !d.figuresPartial) return;
      Object.entries(d.figuresPartial).forEach(([key, value]) => {
        out[key] = { value, docType: type, docTitle: title };
      });
    });
    return out;
  }, [docs]);

  const CONFLICT_TOLERANCE = 1; // rand — avoids flagging float/rounding noise as a real conflict

  const figureConflicts = useMemo(() => {
    return Object.entries(documentFigures)
      .map(([key, doc]) => {
        const existing = Number(figures?.[key]) || 0;
        const hasExisting = Math.abs(existing) > CONFLICT_TOLERANCE;
        const differs = Math.abs(existing - doc.value) > CONFLICT_TOLERANCE;
        return { key, ...doc, existing, isConflict: hasExisting && differs };
      });
  }, [documentFigures, figures]);

  const [resolutions, setResolutions] = useState({}); // { [figureKey]: 'existing' | 'document' }
  const unresolvedConflicts = figureConflicts.filter(c => c.isConflict && !resolutions[c.key]);

  // FEATURE (Sep 2026 — automation-bias gate): a document-sourced figure
  // that never conflicted with anything used to be silently auto-applied
  // with nothing more than a passive "will be used" badge — exactly the
  // pattern that lets a rushed preparer stop actually looking at figures
  // once they carry a "from documents" tag, and start trusting the tag
  // instead of the number. Conflicts already force an active choice
  // (Keep/Use buttons); this extends the same principle to the
  // non-conflicting case specifically, since that's the one that was
  // previously frictionless. Unlike a conflict, this is deliberately NOT
  // a hard block on proceeding — declining to confirm doesn't trap the
  // preparer, it just means that one field doesn't get the auto-fill
  // convenience and is typed in by hand on the next screen instead,
  // exactly as it always could be.
  const [confirmations, setConfirmations] = useState({}); // { [figureKey]: true }

  // The actual figures forwarded onward: existing figures, with every
  // document-sourced key applied only when the preparer has actively
  // engaged with it — either by resolving a genuine conflict, or by
  // confirming a non-conflicting value against its source document.
  // Never applied on a passive, un-clicked "will be used" basis.
  const mergedFigures = useMemo(() => {
    const out = { ...(figures || {}) };
    const documentSourcedKeys = [];
    figureConflicts.forEach(c => {
      if (!c.isConflict && confirmations[c.key]) {
        out[c.key] = c.value;
        documentSourcedKeys.push(c.key);
      } else if (c.isConflict && resolutions[c.key] === 'document') {
        out[c.key] = c.value;
        documentSourcedKeys.push(c.key);
      }
      // Not yet confirmed/resolved: leave out[c.key] as-is (usually blank —
      // the preparer fills it in by hand on the next screen, same as before
      // this feature existed).
    });
    return { figures: out, documentSourcedKeys };
  }, [figures, figureConflicts, resolutions, confirmations]);

  const proceed = async () => {
    if (isProceedingRef.current) return
    isProceedingRef.current = true
    setIsProceeding(true)
    try {
      if (unresolvedConflicts.length > 0) {
        alert(
          `Please resolve ${unresolvedConflicts.length} figure conflict(s) below before continuing — ` +
          `an uploaded document disagrees with an existing figure and KaiLux won't silently pick one for you.`
        )
        return
      }
      const { figures: resolvedFigures, documentSourcedKeys } = mergedFigures;
      if (returnState) {
        navigate("/statements", {
          state: { ...returnState, client: client || returnState?.client || { id: clientId, name: clientLabel }, afsJobId: resolvedAfsJobId, enriched: true },
        })
      } else {
        let autosaveFailedHere = false
        if (resolvedAfsJobId) {
          try {
            await saveJobProgress(resolvedAfsJobId, {
              stage: "documents", accounts, mappings, figures: resolvedFigures, prior_figures,
              first_year, prior_not_available,
            })
          } catch (err) {
            autosaveFailedHere = true
            console.error("Autosave failed (non-fatal — preparer can still continue):", err)
          }
        } else {
          autosaveFailedHere = true
        }
        // FIX (Sep 2026 — document upload now runs before the balance-sheet
        // screen for the bank-statement flow, not after it): forward to
        // /bank-statement/balance-sheet so it can pre-fill from
        // resolvedFigures/documentSourcedKeys (see its own incomingFigures
        // handling), instead of always going to /notes — this flow's
        // balance-sheet entry hasn't happened yet at this point.
        if (bank_statement_source) {
          // FIX (Sep 2026): this page doesn't know about plFigures/
          // directorDrawings/directorRemuneration/relatedPartyItems/
          // relatedPartyLoanMovements/openingCash/netCash — those are
          // BankStatement.jsx/BankStatementBalanceSheet.jsx's own concern.
          // Spread the raw incoming state through untouched so none of it
          // gets silently dropped just because this page passes through the
          // middle now; only figures/documentSourcedKeys/client/
          // autosave_failed are deliberately overridden below with this
          // page's own results.
          navigate('/bank-statement/balance-sheet', {
            state: {
              ...(location.state || {}),
              client: client || { id: clientId, name: clientLabel },
              figures: resolvedFigures, documentSourcedKeys,
              autosave_failed: autosaveFailedHere || autosaveFailed,
            }
          })
          return
        }
        navigate("/notes", {
          state: { client: client || { id: clientId, name: clientLabel }, mappings, accounts, figures: resolvedFigures, prior_figures, first_year, prior_not_available, financial_year: financialYear, afsJobId: resolvedAfsJobId, year_end: yearEnd, period_start: periodStart, period_start_iso, period_end_iso, source, bank_statement_source, autosave_failed: autosaveFailedHere || autosaveFailed },
        })
      }
    } finally {
      isProceedingRef.current = false
      setIsProceeding(false)
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div style={{ minHeight:'100vh', background: T.bg, fontFamily:"'Outfit',sans-serif", color: T.tx }}>
      {/* Spinner keyframe */}
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>

      {/* ── Header ── */}
      <div style={{ background: T.s1, borderBottom:`1px solid ${T.bd}`,
        position:'sticky', top:0, zIndex:10 }}>
        <div style={{ maxWidth:'1100px', margin:'0 auto', padding:'0 28px',
          height:'62px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>

          {/* Breadcrumb + client name */}
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:'4px',
              fontSize:'12px', color: T.mt, marginBottom:'3px' }}>
              <span>{bank_statement_source ? 'Categorize' : 'Upload'}</span>
              <span style={{ color: T.bd }}>›</span>
              <span style={{ color: T.tx, fontWeight:600 }}>Source Documents</span>
              <span style={{ color: T.bd }}>›</span>
              {bank_statement_source && <><span>Balance Sheet</span><span style={{ color: T.bd }}>›</span></>}
              <span>Notes</span>
              <span style={{ color: T.bd }}>›</span>
              <span>Review</span>
              <span style={{ color: T.bd }}>›</span>
              <span>Statements</span>
            </div>
            {clientLabel && (
              <div style={{ fontSize:'14px', fontWeight:700, color: T.tx }}>{clientLabel}</div>
            )}
          </div>

          {/* Actions */}
          <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
            {verifiedCount > 0 && (
              <span style={{ fontSize:'12px', fontWeight:600, color: T.gn,
                background: T.gnd, padding:'4px 10px', borderRadius:'999px' }}>
                {verifiedCount} document{verifiedCount !== 1 ? "s" : ""} verified
              </span>
            )}
            <button onClick={proceed} disabled={unresolvedConflicts.length > 0 || isProceeding}
              style={{ padding:'8px 16px', background:'transparent', border:'none',
                color: unresolvedConflicts.length > 0 ? T.bd : T.mt, fontSize:'14px',
                cursor: (unresolvedConflicts.length > 0 || isProceeding) ? 'not-allowed' : 'pointer',
                fontFamily:"'Outfit',sans-serif" }}>
              Skip & Continue
            </button>
            <button onClick={proceed} disabled={unresolvedConflicts.length > 0 || isProceeding}
              style={{ padding:'8px 18px',
                background: unresolvedConflicts.length > 0 ? T.mt : T.prg,
                border:'none', borderRadius:'8px', color:T.onpri, fontSize:'14px', fontWeight:700,
                cursor: (unresolvedConflicts.length > 0 || isProceeding) ? 'not-allowed' : 'pointer',
                opacity: isProceeding ? 0.7 : 1,
                fontFamily:"'Outfit',sans-serif" }}>
              {unresolvedConflicts.length > 0
                ? `Resolve ${unresolvedConflicts.length} conflict${unresolvedConflicts.length !== 1 ? 's' : ''} first`
                : isProceeding ? 'Saving…' : 'Done — Return to AFS →'}
            </button>
          </div>
        </div>
      </div>

      {/* Bug fix (Sep 2026): surface a previously-silent trial-balance
          autosave failure instead of hiding it — see TrialBalance.jsx's
          proceedToDocuments for where autosave_failed is set. Doesn't block
          anything; just makes sure the preparer knows there's no resumable
          checkpoint for the trial balance step if they leave now. */}
      {autosaveFailed && (
        <div style={{ maxWidth:'1100px', margin:'16px auto 0', padding:'0 28px' }}>
          <div style={{ background:alpha(T.rd, 0.1), border:`1px solid ${T.rd}`,
            borderRadius:'8px', padding:'12px 18px', fontSize:'13px', color: T.rd }}>
            <Icon name="alert" /> Your trial balance categorization couldn't be saved for later resume.
            You can keep working now — but if you leave this AFS before finishing,
            you'll need to re-upload and re-categorize the trial balance.
          </div>
        </div>
      )}

      {/* ── Body ── */}
      <div style={{ maxWidth:'1100px', margin:'0 auto', padding:'40px 28px' }}>

        {/* Hero */}
        <div style={{ marginBottom:'36px' }}>
          <h2 style={{ fontSize:'24px', fontWeight:800, color: T.tx,
            fontFamily:"'Syne',sans-serif", margin:0 }}>
            Upgrade your AFS with source documents
          </h2>
          <p style={{ marginTop:'8px', fontSize:'14px', fontWeight:'400', color:T.mt,
            maxWidth:'640px', lineHeight:'1.7' }}>
            Every document below is optional. Upload what you have — each one upgrades specific
            notes from TB-derived estimates to source-verified figures, clearly marked with a{" "}
            <span style={{ color: T.gn, fontWeight:600 }}>{<Dot color={T.gn} />} source-verified</span>{" "}
            badge throughout the review and PDF. Nothing below blocks your progress.
          </p>
        </div>

        {jobError && (
          <div style={{ marginBottom:'20px', padding:'12px 16px',
            background: T.amd, border:`1px solid ${alpha(T.am, 0.25)}`,
            borderRadius:'8px', fontSize:'14px', color: T.am }}>
            Could not prepare this AFS job: {jobError}. Document upload and manual entry are unavailable —
            you can still use "Skip & Continue" to proceed on the TB-only path.
          </div>
        )}

        {loadError && (
          <div style={{ marginBottom:'20px', padding:'12px 16px',
            background: T.amd, border:`1px solid ${alpha(T.am, 0.25)}`,
            borderRadius:'8px', fontSize:'14px', color: T.am }}>
            Could not load existing documents: {loadError}
          </div>
        )}

        {/* FEATURE (Sep 2026): balance-sheet figures pulled from uploaded
            documents — see document_to_afs_figures() on the backend. Only
            shown once at least one document has produced a figure.
            Conflicts (a document disagreeing with an existing figure) must
            be resolved before Skip & Continue / Done are enabled — see
            unresolvedConflicts above. */}
        {figureConflicts.length > 0 && (
          <div style={{ marginBottom:'28px', padding:'18px 20px', borderRadius:'10px',
            background: T.s2, border:`1px solid ${T.bd}` }}>
            <div style={{ fontSize:'13px', fontWeight:700, color: T.tx, marginBottom:'4px' }}>
              Balance sheet figures from your documents
            </div>
            <div style={{ fontSize:'13px', color: T.mt, marginBottom:'14px', lineHeight:'1.6' }}>
              These figures came from the documents above. Each one needs your confirmation
              before it's used — anything you don't tick is simply left for you to enter by hand
              on the next screen, same as before. Anything that disagrees with an existing figure
              needs your call before you can continue.
            </div>
            {figureConflicts.map(c => (
              <div key={c.key} style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                padding:'10px 0', borderTop:`1px solid ${T.bd}`, gap:'12px', flexWrap:'wrap' }}>
                <div>
                  <div style={{ fontSize:'13px', fontWeight:600, color: T.tx }}>{c.key}</div>
                  <div style={{ fontSize:'12px', color: T.mt }}>from {c.docTitle}</div>
                </div>
                {!c.isConflict ? (
                  <label style={{ display:'flex', alignItems:'center', gap:'8px', cursor:'pointer',
                    fontSize:'12px', fontWeight:600, color: confirmations[c.key] ? T.gn : T.tx,
                    background: confirmations[c.key] ? T.gnd : 'transparent',
                    padding:'4px 10px', borderRadius:'999px',
                    border: confirmations[c.key] ? 'none' : `1px solid ${T.bd}` }}>
                    <input type="checkbox" checked={!!confirmations[c.key]}
                      onChange={e => setConfirmations(prev => ({ ...prev, [c.key]: e.target.checked }))}
                      style={{ margin: 0, cursor:'pointer' }} />
                    {confirmations[c.key]
                      ? <><Icon name="check" /> {`Verified — R${Number(c.value).toLocaleString('en-ZA')} will be used`}</>
                      : `Confirm R${Number(c.value).toLocaleString('en-ZA')} against ${c.docTitle}`}
                  </label>
                ) : resolutions[c.key] ? (
                  <span style={{ fontSize:'12px', fontWeight:600, color: T.gn,
                    background: T.gnd, padding:'4px 10px', borderRadius:'999px', cursor:'pointer' }}
                    onClick={() => setResolutions(prev => { const n = { ...prev }; delete n[c.key]; return n })}>
                    <Icon name="check" /> Using {resolutions[c.key] === 'document' ? 'document' : 'existing'} value
                    (R{Number(resolutions[c.key] === 'document' ? c.value : c.existing).toLocaleString('en-ZA')}) — change
                  </span>
                ) : (
                  <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                    <span style={{ fontSize:'12px', color: T.am, fontWeight:600 }}>Conflict:</span>
                    <button onClick={() => setResolutions(prev => ({ ...prev, [c.key]: 'existing' }))}
                      style={{ padding:'6px 12px', borderRadius:'6px', border:`1px solid ${T.bd}`,
                        background:'transparent', color: T.tx, fontSize:'12px', cursor:'pointer',
                        fontFamily:"'Outfit',sans-serif" }}>
                      Keep existing: R{c.existing.toLocaleString('en-ZA')}
                    </button>
                    <button onClick={() => setResolutions(prev => ({ ...prev, [c.key]: 'document' }))}
                      style={{ padding:'6px 12px', borderRadius:'6px', border:'none',
                        background: T.am, color:T.bg, fontSize:'12px', fontWeight:700, cursor:'pointer',
                        fontFamily:"'Outfit',sans-serif" }}>
                      Use document: R{Number(c.value).toLocaleString('en-ZA')}
                    </button>
                  </div>
                )}
              </div>
            ))}
            {(() => {
              const pendingCount = figureConflicts.filter(c => !c.isConflict && !confirmations[c.key]).length
              return pendingCount > 0 ? (
                <div style={{ marginTop:'12px', paddingTop:'12px', borderTop:`1px solid ${T.bd}`,
                  fontSize:'12px', color: T.mt }}>
                  {pendingCount} figure{pendingCount !== 1 ? 's' : ''} not yet confirmed — you can still
                  continue; unconfirmed figures are left blank for manual entry on the next screen.
                </div>
              ) : null
            })()}
          </div>
        )}

        {loading ? (
          <div style={{ display:'flex', alignItems:'center', gap:'8px',
            fontSize:'14px', color: T.mt }}>
            <SpinnerIcon style={{ width:16, height:16, color: T.mt,
              animation:'spin 1s linear infinite' }} />
            Loading…
          </div>
        ) : (
          <>
            {/* High impact section */}
            <section style={{ marginBottom:'36px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'16px' }}>
                <h3 style={{ fontSize:'10px', fontWeight:700, color: T.lt,
                  textTransform:'uppercase', letterSpacing:'0.1em', margin:0 }}>High impact</h3>
                <div style={{ flex:1, height:'1px', background: T.bd }} />
                <span style={{ fontSize:'12px', color: T.mt }}>Directly enriches specific notes</span>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:'12px' }}>
                {HIGH_IMPACT.map(def => (
                  <DocumentCard key={def.type} def={def} state={docs[def.type]}
                    onUpload={handleUpload} onDelete={handleDelete} onManualSave={handleManualSave} />
                ))}
              </div>
            </section>

            {/* Reasonableness section */}
            <section>
              <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'16px' }}>
                <h3 style={{ fontSize:'10px', fontWeight:700, color: T.lt,
                  textTransform:'uppercase', letterSpacing:'0.1em', margin:0 }}>Reasonableness checks</h3>
                <div style={{ flex:1, height:'1px', background: T.bd }} />
                <span style={{ fontSize:'12px', color: T.mt }}>Verifies and enriches contextual notes</span>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:'12px' }}>
                {SUPPORTING.map(def => (
                  <DocumentCard key={def.type} def={def} state={docs[def.type]}
                    onUpload={handleUpload} onDelete={handleDelete} onManualSave={handleManualSave} />
                ))}
              </div>
            </section>
          </>
        )}

        {/* Bottom CTA */}
        <div style={{ marginTop:'48px', paddingTop:'28px',
          borderTop:`1px solid ${T.bd}`, display:'flex',
          alignItems:'center', justifyContent:'space-between' }}>
          <div>
            {verifiedCount > 0 ? (
              <p style={{ fontSize:'14px', color: T.mt, margin:0 }}>
                <span style={{ fontWeight:700, color: T.gn }}>
                  {verifiedCount} document{verifiedCount !== 1 ? "s" : ""}
                </span>{" "}
                verified — your notes will use real source data wherever available.
              </p>
            ) : (
              <p style={{ fontSize:'14px', color: T.mt, margin:0 }}>
                No documents uploaded — notes will use TB-derived values throughout.
              </p>
            )}
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
            <button onClick={proceed}
              style={{ padding:'9px 18px', background:'transparent', border:'none',
                color: T.mt, fontSize:'14px', cursor:'pointer',
                fontFamily:"'Outfit',sans-serif" }}>
              Skip & Continue
            </button>
            <button onClick={proceed}
              style={{ padding:'10px 22px', background:T.prg,
                border:'none', borderRadius:'8px', color:T.onpri, fontSize:'14px', fontWeight:700,
                cursor:'pointer', fontFamily:"'Outfit',sans-serif" }}>
              Done — Return to AFS →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
